
import React, { createContext, useContext, useState, useEffect } from 'react';
import { CreditCardBill } from '@/types/bill';
import { useTransactions } from '@/contexts/transactions';
import { useCards } from '@/contexts/cards';
import { useAuth } from '@/contexts/auth';
import { CreditCardBillsContextType } from './credit-card-bills/types';
import { createBillForCard, removeTransactionFromBill } from './credit-card-bills/billOperations';
import { generateFutureBillsForCard } from '@/utils/cardBillUtils';
import {
  saveBillToSupabase,
  loadBillsFromSupabase,
  deleteBillFromSupabase,
} from '@/lib/supabase-bills-operations';
import { migrateBillsToSupabase } from '@/lib/data-migration';
import { logger } from '@/utils/logger';

const CreditCardBillsContext = createContext<CreditCardBillsContextType | undefined>(undefined);

export const CreditCardBillsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [bills, setBills] = useState<CreditCardBill[]>([]);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { getCardById } = useCards();
  const { user } = useAuth();
  const transactionsContext = useTransactions();

  // Update transactions state when context changes
  useEffect(() => {
    setTransactions(transactionsContext.transactions);
  }, [transactionsContext.transactions]);

  // Load data when user changes
  useEffect(() => {
    const loadData = async () => {
      if (!user) {
        setBills([]);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Perform migration first
        await migrateBillsToSupabase();
        
        // Load from Supabase (user is already authenticated via AuthContext)
        logger.debug('[CreditCardBills] Loading bills from Supabase...');
        const supabaseBills = await loadBillsFromSupabase();
        setBills(supabaseBills);
        logger.info(`[CreditCardBills] Loaded ${supabaseBills.length} bills`);
      } catch (error) {
        logger.error('[CreditCardBills] Error loading bills:', error);
        setError('Erro ao carregar faturas');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [user]);

  // Note: Individual bills are saved when created/updated via saveBillToSupabase
  // No need for a separate sync effect

  const generateBillForCard = async (cardId: string, periodEnd: string): Promise<CreditCardBill | null> => {
    const card = getCardById(cardId);
    if (!card) return null;

    // Calculate period start (previous month)
    const endDate = new Date(periodEnd);
    const startDate = new Date(endDate);
    startDate.setMonth(startDate.getMonth() - 1);
    startDate.setDate(startDate.getDate() + 1);

    // Get pending transactions for this card in the period
    const cardTransactions = transactions.filter(t => 
      t.cardId === cardId && 
      t.status === 'pending' &&
      t.type === 'despesa' &&
      new Date(t.date) >= startDate &&
      new Date(t.date) <= endDate
    );

    if (cardTransactions.length === 0) return null;

    const newBill = createBillForCard(cardId, periodEnd, cardTransactions, card.dueDate);
    
    try {
      await saveBillToSupabase(newBill);
      logger.debug('[CreditCardBills] Bill saved:', newBill.id);
      
      setBills(prev => [...prev, newBill]);
      return newBill;
    } catch (error) {
      logger.error('[CreditCardBills] Error saving bill:', error);
      setError('Erro ao criar fatura');
      throw error;
    }
  };

  const getBillById = (billId: string) => {
    return bills.find(bill => bill.id === billId);
  };

  const getBillsByCard = (cardId: string) => {
    return bills.filter(bill => bill.cardId === cardId);
  };

  const getCurrentBillForCard = (cardId: string) => {
    const cardBills = getBillsByCard(cardId);
    return cardBills.find(bill => bill.status !== 'paga') || cardBills[cardBills.length - 1];
  };

  const getAllBillsForCard = (cardId: string) => {
    // Combinar faturas existentes com faturas futuras geradas automaticamente
    const existingBills = getBillsByCard(cardId);
    const futureBills = generateFutureBillsForCard(cardId, transactions, [getCardById(cardId)].filter(Boolean));
    
    // Converter faturas futuras para o formato CreditCardBill
    const formattedFutureBills: CreditCardBill[] = futureBills.map(bill => ({
      id: bill.id,
      cardId: bill.cardId,
      periodStart: bill.periodStart,
      periodEnd: bill.periodEnd,
      dueDate: bill.dueDate,
      totalAmount: bill.totalAmount,
      paidAmount: bill.paidAmount,
      status: bill.status as 'pendente' | 'paga' | 'parcialmente_paga',
      transactionIds: bill.transactionIds,
      paymentIds: [],
      createdAt: new Date().toISOString()
    }));

    // Filtrar faturas futuras que já existem nas faturas criadas
    const existingDueDates = existingBills.map(b => b.dueDate);
    const newFutureBills = formattedFutureBills.filter(fb => !existingDueDates.includes(fb.dueDate));
    
    // Combinar e ordenar por data de vencimento
    const allBills = [...existingBills, ...newFutureBills].sort((a, b) => 
      new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()
    );
    
    return allBills;
  };

  // Função para remover transação de faturas
  const removeTransactionFromBills = async (transactionId: string) => {
    logger.debug(`[CreditCardBills] Removing transaction ${transactionId} from bills`);
    
    const updatedBills = bills.map(bill => {
      if (bill.transactionIds.includes(transactionId)) {
        const updatedBill = removeTransactionFromBill(bill, transactionId);
        logger.debug(`[CreditCardBills] Removed from bill ${bill.id}. New total: ${updatedBill.totalAmount}`);
        
        saveBillToSupabase(updatedBill).catch(error => {
          logger.error('[CreditCardBills] Error saving updated bill:', error);
        });
        
        return updatedBill;
      }
      return bill;
    });
    
    setBills(updatedBills);
  };

  return (
    <CreditCardBillsContext.Provider
      value={{
        bills,
        generateBillForCard,
        getBillById,
        getBillsByCard,
        getCurrentBillForCard,
        getAllBillsForCard,
        removeTransactionFromBills,
        isLoading,
        error
      }}
    >
      {children}
    </CreditCardBillsContext.Provider>
  );
};

export const useCreditCardBills = () => {
  const context = useContext(CreditCardBillsContext);
  if (context === undefined) {
    throw new Error('useCreditCardBills must be used within a CreditCardBillsProvider');
  }
  return context;
};
